import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { forgotPasswordSchema, type ForgotPasswordInput } from '@cmms/validation'
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { Link as RouterLink } from 'react-router-dom'
import { AuthShell } from '../../layouts/AuthShell'
import { api } from '../../lib/api'
import { ApiError } from '@cmms/api-client'

export function ForgotPasswordPage() {
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { org_slug: '', email: '' },
  })

  async function onSubmit(values: ForgotPasswordInput) {
    setError(null)
    try {
      await api.auth.forgotPassword(values)
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Request failed')
    }
  }

  return (
    <AuthShell
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a reset link."
      swap={{ prompt: 'Remembered it?', cta: 'Log In', to: '/login' }}
    >
      {done ? (
        <Stack spacing={2}>
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            If that account exists, a reset link has been sent.
          </Alert>
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
            Back to sign in
          </Link>
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2.5}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Organization
              </Typography>
              <TextField
                fullWidth
                placeholder="your-org"
                error={!!errors.org_slug}
                helperText={errors.org_slug?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <DomainOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('org_slug')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Email address
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="john@example.com"
                error={!!errors.email}
                helperText={errors.email?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('email')}
              />
            </Box>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
                Back to sign in
              </Link>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{ borderRadius: 999, px: 4, minWidth: 160 }}
              >
                {isSubmitting ? 'Sending...' : 'Send reset link'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </AuthShell>
  )
}
